import React from 'react';
import { Star, ChevronRight, Activity, ShieldCheck, Cpu } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { heroData, clientAvatars } from '../data/mockData';

const HeroSection = () => {
  const navigate = useNavigate();
  // Animation Variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.8 // Wait for splash screen fade
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: 'spring', stiffness: 100 }
    }
  };

  const floatingVariants = {
    animate: {
      y: [0, -15, 0],
      rotate: [0, 2, 0],
      transition: {
        duration: 4,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  return (
    <section id="home" data-theme="dark" className="relative min-h-screen bg-[#0d0d10] overflow-hidden flex items-center justify-center py-20 lg:py-0">
      {/* Dynamic Background elements */}
      <div className="absolute top-0 right-0 w-[300px] sm:w-[600px] h-[300px] sm:h-[600px] bg-[#3bd07a]/15 rounded-full blur-[80px] sm:blur-[120px] -mr-32 sm:-mr-64 -mt-16 sm:-mt-32 animate-pulse" />
      <div className="absolute bottom-0 left-0 w-[400px] sm:w-[800px] h-[400px] sm:h-[800px] bg-gradient-to-tr from-[#3bd07a]/20 via-blue-600/10 to-transparent rounded-full blur-[100px] sm:blur-[140px] -ml-48 sm:-ml-96 -mb-32 sm:-mb-64" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full mt-6 sm:mt-12 lg:mt-24">
        <div className="grid lg:grid-cols-2 gap-12 sm:gap-20 xl:gap-32 items-center">

          {/* Left Content */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="flex flex-col text-center lg:text-left items-center lg:items-start order-1"
          >
            <motion.div variants={itemVariants} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 w-fit mb-8 sm:mb-10 shadow-inner">
              <span className="flex h-2 w-2 rounded-full bg-[#3bd07a] animate-pulse" />
              <span className="text-[10px] sm:text-[11px] font-extrabold text-[#3bd07a] uppercase tracking-[0.3em]">SECURE & RESPONSIBLE WEALTH</span>
            </motion.div>

            <motion.h1 variants={itemVariants} className="text-3xl sm:text-5xl lg:text-7xl font-bold text-white leading-[1.1] mb-6 tracking-tight">
              {heroData.title.split(' ').map((word, i) => (
                <span key={i} className={i > 3 ? "text-[#3bd07a] italic underline underline-offset-[8px] decoration-[#3bd07a]/20" : ""}>{word} </span>
              ))}
            </motion.h1>

            <motion.p variants={itemVariants} className="text-white/60 text-base sm:text-xl max-w-xl leading-relaxed mb-10">
              {heroData.description}
            </motion.p>

            <motion.div variants={itemVariants} className="flex flex-wrap justify-center lg:justify-start gap-6">
              <button
                onClick={() => navigate('/coming-soon')}
                className="relative group bg-[#3bd07a] text-[#0d0d10] font-bold px-8 sm:px-10 py-4 sm:py-5 rounded-2xl overflow-hidden transition-all hover:scale-105 active:scale-95 shadow-[0_20px_40px_-10px_rgba(59,208,122,0.4)]"
              >
                <span className="relative z-10 flex items-center gap-2 text-sm sm:text-base">
                  {heroData.buttonText}
                  <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </span>
                <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity" />
              </button>
            </motion.div>
          </motion.div>

          {/* Right Content - Advanced 3D-like Mockups */}
          <div className="relative h-[480px] sm:h-[600px] flex items-center justify-center lg:block order-2 scale-[0.9] sm:scale-100 mb-12 lg:mb-0">

            {/* Main Interactive Container */}
            <motion.div
              style={{ perspective: 1000 }}
              className="relative w-full h-full"
            >
              {/* Backglow for the units */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] sm:w-[400px] h-[300px] sm:h-[400px] bg-blue-500/20 rounded-full blur-[80px] sm:blur-[100px] transition-all" />

              {/* Floating Dashboard Unit - Compact for Mobile */}
              <motion.div
                variants={floatingVariants}
                animate="animate"
                className="absolute top-0 -right-2 sm:-right-4 z-20 w-[180px] sm:w-[320px] p-1 bg-gradient-to-br from-white/20 to-transparent rounded-[22px] sm:rounded-3xl backdrop-blur-3xl border border-white/20 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.5)] scale-[0.8] sm:scale-100"
              >
                <div className="bg-[#0d0d10]/90 p-4 sm:p-5 rounded-[22px]">
                  <div className="flex justify-between items-center mb-4 sm:mb-6">
                    <div className="flex items-center gap-2">
                      <div className="w-6 sm:w-8 h-6 sm:h-8 rounded-lg bg-[#3bd07a]/20 flex items-center justify-center">
                        <Activity className="w-3 sm:w-4 h-3 sm:h-4 text-[#3bd07a]" />
                      </div>
                      <span className="text-xs sm:text-sm font-bold text-white">AI Health Index</span>
                    </div>
                    <span className="text-[8px] sm:text-[10px] text-white/40 bg-white/5 px-2 py-0.5 sm:py-1 rounded-md">LIVE</span>
                  </div>

                  <div className="space-y-2 sm:space-y-4">
                    <div className="flex justify-between items-end">
                      <div className="text-xl sm:text-4xl font-bold text-white tracking-tighter">82%</div>
                      <div className="text-[7px] sm:text-[10px] text-[#3bd07a] mb-0.5 sm:mb-1 font-extrabold tracking-wider">+12.4% OPTIMAL</div>
                    </div>

                    <div className="h-1.5 sm:h-2 w-full bg-white/5 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: "82%" }}
                        transition={{ duration: 1.5, delay: 1 }}
                        className="h-full bg-gradient-to-r from-blue-500 to-[#3bd07a]"
                      />
                    </div>

                    <div className="bg-white/5 p-2 sm:p-3 rounded-xl border border-white/5">
                      <div className="text-[8px] sm:text-[10px] text-white/40 mb-1">Recommended Action</div>
                      <div className="text-[10px] sm:text-xs text-white/80 leading-relaxed">Re-allocate 12% of idle cash to Liquid Debt Fund</div>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Floating Shield Unit - Compact for Mobile */}
              <motion.div
                animate={{
                  y: [0, 15, 0],
                  x: [0, -5, 0],
                  rotate: [3, -1, 3]
                }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                className="absolute bottom-16 -left-2 sm:-left-8 z-30 w-[140px] sm:w-[220px] p-3 sm:p-4 rounded-xl sm:rounded-2xl bg-white/5 backdrop-blur-2xl border border-white/10 shadow-2xl scale-[0.85] sm:scale-100"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 sm:w-10 h-8 sm:h-10 rounded-full bg-[#3bd07a] flex items-center justify-center shadow-[0_0_20px_rgba(59,208,122,0.4)]">
                    <ShieldCheck className="w-5 sm:w-6 h-5 sm:h-6 text-[#0d0d10]" />
                  </div>
                  <div>
                    <div className="text-xs sm:text-sm font-bold text-white">Protected</div>
                    <div className="text-[8px] sm:text-[10px] text-white/50 tracking-wide uppercase">Consent-Locked</div>
                  </div>
                </div>
              </motion.div>

              {/* Advanced Phone Mockup */}
              <motion.div
                initial={{ rotateY: -15, rotateX: 5 }}
                whileHover={{ rotateY: 0, rotateX: 0, scale: 1.05 }}
                transition={{ type: 'spring', stiffness: 40 }}
                className="relative mx-auto w-[160px] sm:w-[240px] h-[320px] sm:h-[480px] bg-[#1a1a1f] rounded-[40px] sm:rounded-[48px] p-[6px] sm:p-[8px] shadow-[0_50px_100px_rgba(0,0,0,0.6)] border border-white/10 overflow-hidden"
              >
                <div className="absolute inset-x-0 top-0 h-6 sm:h-7 bg-[#0d0d10] z-20 flex justify-center items-end pb-1">
                  <div className="w-16 sm:w-20 h-3 sm:h-4 bg-black rounded-b-xl" />
                </div>

                <div className="w-full h-full bg-[#0d0d10] rounded-[32px] sm:rounded-[40px] overflow-hidden pt-8 px-4">
                  <div className="flex justify-between items-center opacity-40 mb-6">
                    <span className="text-[8px] sm:text-[9px] font-bold text-white">ZenCap Mobile</span>
                    <Cpu className="w-3 h-3 text-white" />
                  </div>

                  <div className="mb-5">
                    <div className="text-[8px] sm:text-[9px] text-white/40 mb-1 font-mono uppercase tracking-widest">Financial Health Index</div>
                    <div className="text-xl sm:text-2xl font-bold text-white">82 <span className="text-[#3bd07a] text-xs">/100</span></div>
                  </div>

                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="p-3 rounded-xl bg-white/5 border border-white/5 space-y-2">
                        <div className="h-1.5 w-1/2 bg-white/20 rounded-full" />
                        <div className="h-1.5 w-full bg-white/10 rounded-full" />
                      </div>
                    ))}
                  </div>

                  <div className="absolute bottom-6 inset-x-4">
                    <div className="w-full py-3 rounded-xl bg-blue-600 text-white font-bold text-[8px] sm:text-[10px] shadow-lg text-center">
                      View ADI Analysis
                    </div>
                  </div>
                </div>

                {/* Glass reflections */}
                <div className="absolute inset-0 bg-gradient-to-tr from-white/10 to-transparent pointer-events-none" />
              </motion.div>

            </motion.div>
          </div>

        </div>
      </div>

      {/* Scroll Indicator - Hidden on small mobile */}
      <motion.div
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 hidden sm:flex flex-col items-center gap-2"
      >
        <div className="w-5 h-8 rounded-full border border-white/20 flex justify-center p-1">
          <div className="w-1 h-2 bg-[#3bd07a] rounded-full" />
        </div>
      </motion.div>
    </section>
  );
};

export default HeroSection;